import { Link } from "react-router-dom";
import {
  Building2,
  Clock,
  Download,
  FileText,
  Flag,
  Globe,
  Mail,
  MapPin,
  Phone,
  User,
  Users,
  Video,
  X,
} from "lucide-react";
import Button from "../../../components/Button/Button";
import {
  TypeBadge,
  StatusBadge,
  applicantName,
  applicationCode,
  formatDate,
  downloadApplicationFile,
} from "./applicationParts";

const initials = (a) =>
  `${a.first_name?.[0] || ""}${a.last_name?.[0] || ""}`.toUpperCase() || "?";

const formatSize = (bytes) => {
  if (!bytes) return null;
  return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`;
};

const extension = (name) => (name.includes(".") ? name.split(".").pop().toUpperCase() : "FILE");

const websiteHref = (site) => (/^https?:\/\//i.test(site) ? site : `https://${site}`);

const SectionTitle = ({ children, count }) => (
  <h3 className="mb-2.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
    {children}
    {count !== undefined && (
      <span className="rounded-full bg-gray-100 px-1.5 py-px text-[10px] font-semibold text-gray-500">{count}</span>
    )}
  </h3>
);

const Chips = ({ items, empty }) =>
  items.length ? (
    <div className="flex flex-wrap gap-1.5">
      {items.map((item) => (
        <span
          key={item.key}
          className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700"
        >
          {item.label}
        </span>
      ))}
    </div>
  ) : (
    <p className="text-xs text-gray-400">{empty}</p>
  );

// Only details the applicant filled in are listed.
const InfoRow = ({ icon: Icon, label, children }) =>
  children ? (
    <div className="flex items-start gap-3 py-2">
      <Icon size={15} className="mt-0.5 shrink-0 text-gray-400" />
      <div className="min-w-0">
        <p className="text-[11px] text-gray-500">{label}</p>
        <p className="break-words text-[13px] text-gray-900">{children}</p>
      </div>
    </div>
  ) : null;

const ApplicationDetailModal = ({ application: a, canReview, onAccept, onReject, onClose }) => {
  const pending = canReview && a.status === "new";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4 backdrop-blur-[2px]">
      <div
        className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5"
        role="dialog"
        aria-modal="true"
      >
        <div className="relative border-b border-gray-100 bg-gradient-to-b from-gray-50 to-white px-6 py-5">
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
          >
            <X size={18} />
          </button>
          <div className="flex items-center gap-4 pr-8">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-brand-600 text-base font-semibold text-white">
              {initials(a)}
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="truncate text-lg font-semibold tracking-tight text-gray-900">
                  {applicantName(a)}
                </h2>
                <TypeBadge type={a.type} />
                <StatusBadge status={a.status} />
              </div>
              <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[13px] text-gray-500">
                <span>{a.email}</span>
                <span className="text-gray-300">|</span>
                <span>
                  {applicationCode(a)} · Applied {formatDate(a.createdAt)}
                </span>
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_280px]">
            <div className="space-y-6 px-6 py-5">
              <section>
                <SectionTitle count={a.language_pairs.length}>Language Pairs</SectionTitle>
                <Chips
                  items={a.language_pairs.map((lp) => ({ key: lp.id, label: lp.label }))}
                  empty="No language pairs selected."
                />
              </section>

              <section>
                <SectionTitle count={a.specializations.length}>Specializations</SectionTitle>
                <Chips
                  items={a.specializations.map((s) => ({ key: s.id, label: s.name }))}
                  empty="No specializations selected."
                />
              </section>

              <section>
                <SectionTitle>Note</SectionTitle>
                {a.note ? (
                  <p className="whitespace-pre-wrap rounded-lg border-l-2 border-brand-200 bg-gray-50 px-4 py-3 text-[13px] leading-relaxed text-gray-700">
                    {a.note}
                  </p>
                ) : (
                  <p className="text-xs text-gray-400">No note added.</p>
                )}
              </section>

              <section>
                <SectionTitle count={a.files.length}>Files</SectionTitle>
                {a.files.length ? (
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {a.files.map((file) => (
                      <button
                        key={file.id}
                        type="button"
                        onClick={() => downloadApplicationFile(file)}
                        className="group flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-left transition hover:border-brand-300 hover:bg-brand-50/40"
                      >
                        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-gray-100 text-gray-500 group-hover:bg-brand-100 group-hover:text-brand-600">
                          <FileText size={16} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[13px] font-medium text-gray-900">
                            {file.file_name}
                          </span>
                          <span className="text-[11px] text-gray-500">
                            {[extension(file.file_name), formatSize(file.file_size)].filter(Boolean).join(" · ")}
                          </span>
                        </span>
                        <Download size={15} className="shrink-0 text-gray-400 group-hover:text-brand-600" />
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400">No files uploaded.</p>
                )}
              </section>
            </div>

            <aside className="border-t border-gray-100 bg-gray-50/60 px-5 py-5 md:border-l md:border-t-0">
              <SectionTitle>Details</SectionTitle>
              <div className="divide-y divide-gray-100">
                <InfoRow icon={Building2} label="Company">{a.company_name}</InfoRow>
                <InfoRow icon={MapPin} label="Country">{a.country}</InfoRow>
                <InfoRow icon={Globe} label="Website">
                  {a.website && (
                    <a
                      href={websiteHref(a.website)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-brand-600 hover:underline"
                    >
                      {a.website}
                    </a>
                  )}
                </InfoRow>
                <InfoRow icon={Mail} label="Email">{a.email}</InfoRow>
                <InfoRow icon={Phone} label="Phone">{a.phone}</InfoRow>
                <InfoRow icon={Clock} label="Timezone">{a.timezone}</InfoRow>
                <InfoRow icon={User} label="Gender">{a.gender}</InfoRow>
                <InfoRow icon={Flag} label="Nationality">{a.nationality}</InfoRow>
                <InfoRow icon={Video} label="Zoom ID">{a.zoom_id}</InfoRow>
                <InfoRow icon={Users} label="Teams ID">{a.teams_id}</InfoRow>
              </div>
            </aside>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 bg-white px-6 py-3.5">
          {a.status === "accepted" && a.vendor_id ? (
            <p className="text-[13px] text-gray-600">
              Accepted as a vendor.{" "}
              <Link to={`/vendors/${a.vendor_id}`} className="font-medium text-brand-600 hover:underline">
                Open vendor
              </Link>
            </p>
          ) : a.status === "rejected" ? (
            <p className="text-[13px] text-gray-500">This application was rejected.</p>
          ) : (
            <p className="text-[13px] text-gray-500">Review the application before accepting it.</p>
          )}
          <div className="flex gap-2">
            {pending ? (
              <>
                <Button variant="dangerSoft" size="sm" onClick={onReject}>
                  Reject
                </Button>
                <Button size="sm" onClick={onAccept}>
                  Accept Application
                </Button>
              </>
            ) : (
              <Button variant="secondary" size="sm" onClick={onClose}>
                Close
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationDetailModal;
